// @file Composable to scroll to newest post when it appears
import { awaitDOMUpdates } from '@@/bits/dom'
import { WallWishGroupByTypes } from '@@/enums'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import { useReducedMotionMode } from '@@/vuecomposables/useReducedMotionMode'
import { storeToRefs } from 'pinia'
import type { Ref } from 'vue'
import { onBeforeUnmount, onMounted } from 'vue'

// useScrollToNewestPost watches for changes in a given container
// and scrolls to the newest post when it is published.
export function useScrollToNewestPost(parentContainer: Ref<HTMLElement | null>): void {
  const surfaceStore = useSurfaceStore()
  const surfacePostsStore = useSurfacePostsStore()
  const surfaceWishArrangementStore = useSurfaceWishArrangementStore()

  const { user, isMatrix, isGrid, isTable, canUseSections } = storeToRefs(surfaceStore)
  const { wishGroupBy } = storeToRefs(surfaceWishArrangementStore)
  const { newestPostCid } = storeToRefs(surfacePostsStore)

  const { isInReducedMotionMode } = useReducedMotionMode(user)

  let sectionsObserver: MutationObserver | null = null
  const postsObservers: Map<string, MutationObserver> = new Map()

  let scrollTimeout: NodeJS.Timeout | null = null

  // Adds a mutation observer to a (descendant of a) section to
  // watch for new posts and scrolls to the newest post when it appears.
  const addPostsObserver = (node: Element): void => {
    const attributesToWatch = ['data-post-cid', 'data-cid']
    const groupPostsObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type !== 'childList') return

        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return

          for (const attr of attributesToWatch) {
            if (!node.hasAttribute(attr)) continue

            const elementCid = node.getAttribute(attr)
            if (elementCid !== newestPostCid.value) continue

            // Cancel any pending scrolls
            if (scrollTimeout != null) {
              clearTimeout(scrollTimeout)
            }

            // Enqueue a delayed scroll to the newest post
            scrollTimeout = setTimeout(() => {
              scrollToNewestPost(node)
              node.focus()
            }, 200)
            break
          }
        })
      })
    })

    // Start observing the node
    groupPostsObserver.observe(node, {
      childList: true,
    })

    // Add the observer to the map
    postsObservers.set(node.id, groupPostsObserver)
  }

  const addTableSectionObserver = (node: Element): void => {
    const tableSectionObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type !== 'childList') return
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return

          // group-posts is the parent container of the posts in a section
          const groupPostsEl = node.querySelector('[id^="group-posts-"]')
          if (groupPostsEl == null) return
          addPostsObserver(groupPostsEl)
        })
      })
    })
    tableSectionObserver.observe(node, {
      childList: true,
    })
  }

  const getScrollBehavior = (node: HTMLElement): ScrollBehavior => {
    if (isInReducedMotionMode.value) return 'auto'

    // We only use smooth scrolling if the post is sufficiently close
    // We do this for performance, so as to avoid rendering all the posts
    // as the page is scrolled.
    const nodeRect = node.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const viewportWidth = window.innerWidth

    const dy = Math.abs(window.scrollY - nodeRect.top)
    const dx = Math.abs(window.scrollX - nodeRect.left)
    const shouldSmoothScroll = dy < viewportHeight * 4 && dx < viewportWidth * 4

    return shouldSmoothScroll ? 'smooth' : 'auto'
  }

  const scrollToNewestPost = (node: HTMLElement): void => {
    // For certain formats, the posts are absolutely positioned and the position of the posts
    // are calculated on the fly. So we need to watch for position changes and scroll to the
    // newest post whenever the position changes.
    if (isMatrix.value || isGrid.value) {
      let lastOffsetTop = 0

      const scrollWithOffset = (): void => {
        if (node.offsetTop > 0 && node.offsetTop !== lastOffsetTop) {
          lastOffsetTop = node.offsetTop
          void awaitDOMUpdates().then(() => {
            node.scrollIntoView({
              behavior: getScrollBehavior(node),
              block: isTable.value ? 'center' : 'start',
              inline: 'nearest',
            })
          })
        }
      }

      // Try to scroll immediately first
      scrollWithOffset()

      // Observe the position of the newest post,
      // and scroll to it when it changes
      const positionObserver = new MutationObserver(scrollWithOffset)

      positionObserver.observe(node, {
        attributes: true,
        attributeFilter: ['style'],
      })

      // If there are a lot of posts, it may take some some time for all of them to be loaded and positioned.
      // We will keep observing the position of the newest posts and scrolling to it for at most 10 seconds.
      setTimeout(() => {
        positionObserver.disconnect()
      }, 10_000)
    } else {
      void awaitDOMUpdates().then(() => {
        node.scrollIntoView({
          behavior: getScrollBehavior(node),
          block: isTable.value ? 'center' : 'start',
          inline: 'nearest',
        })
      })
    }
  }

  onMounted(() => {
    if (parentContainer.value == null) return

    if (isGrid.value && (!canUseSections.value || wishGroupBy.value === WallWishGroupByTypes.None)) {
      // GridMasonry without sections has a single parent container
      // that contains all the posts directly
      addPostsObserver(parentContainer.value)
      return
    }

    // Add postsObserver for each existing section
    parentContainer.value.querySelectorAll('[id^="section-"]').forEach((el) => {
      if (isTable.value) {
        addTableSectionObserver(el)
      }
      const postsContainer = el.querySelector('[id^="group-posts-"]')
      if (postsContainer != null) {
        addPostsObserver(postsContainer)
      }
    })

    // Create an observer to watch for new sections and add postsObservers to them
    sectionsObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type !== 'childList') return

        // Add new postsObservers when sections are added
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return

          // Check if this is a section node
          if (!node.id?.startsWith('section-')) return

          if (isTable.value) {
            // For table format, the group-posts do not exist until at least one post is added
            addTableSectionObserver(node)
          } else {
            // group-posts is the parent container of the posts in a section
            const groupPostsEl = node.querySelector('[id^="group-posts-"]')
            if (groupPostsEl == null) return
            addPostsObserver(groupPostsEl)
          }
        })

        // When section is removed, disconnect its observer
        mutation.removedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) return
          if (!node.id?.startsWith('section-')) return

          const observer = postsObservers.get(node.id)
          if (observer != null) {
            observer.disconnect()
          }
          postsObservers.delete(node.id)
        })
      })
    })

    sectionsObserver.observe(parentContainer.value, {
      childList: true,
    })
  })

  onBeforeUnmount(() => {
    if (scrollTimeout != null) {
      clearTimeout(scrollTimeout)
      scrollTimeout = null
    }

    postsObservers.forEach((observer) => {
      observer.disconnect()
    })
    postsObservers.clear()

    if (sectionsObserver != null) {
      sectionsObserver.disconnect()
      sectionsObserver = null
    }
  })
}
